import { useEffect, useMemo, useState } from "react";
import {
  Settings as SettingsIcon, Loader2, AlertCircle, Check, RotateCcw, Hash, Lock, Save,
} from "lucide-react";
import {
  usePortalSettingsQuery,
  useSavePortalSettingsMutation,
  type PortalSetting,
  type PortalSettingGroup,
  type SeriesState,
} from "../api/portalSettingsApi";

/**
 * Portal settings — the values the secretariat changes without a deploy.
 *
 * Every field shows what it is worth NOW and where that came from: a value
 * typed in here, or the default the code ships with. That distinction is the
 * point of the screen as much as the number is — an admin looking at a fee
 * needs to know whether it is the Institute's decision or a fallback nobody
 * has ever confirmed.
 *
 * Nothing saves as you type. The values are read together — a prefix and a
 * width are one decision about what a number looks like — so they are edited
 * together and written in one batch, which the server applies in one
 * transaction.
 */

const SOURCE_LABEL: Record<string, string> = {
  settings: "Set here",
  default: "Default",
};

/** Naira, as the rest of the portal writes it. */
const naira = (n: number) => `₦${Number(n).toLocaleString("en-NG")}`;

function Unit({ unit }: { unit?: string | null }) {
  if (!unit) return null;
  const label = unit === "naira" ? "₦" : unit;
  return (
    <span className="text-xs font-bold text-gray-400 shrink-0">{label}</span>
  );
}

/** What a field falls back to if it is cleared, in the reader's language. */
function defaultHint(s: PortalSetting) {
  if (s.default === null || s.default === "") {
    return "Leave blank for no requirement.";
  }
  const shown = s.unit === "naira" ? naira(Number(s.default)) : String(s.default);
  return `Blank falls back to ${shown}.`;
}

function Field({
  setting, draft, onChange,
}: {
  setting: PortalSetting;
  draft: string;
  onChange: (v: string) => void;
}) {
  const dirty = draft !== toInput(setting.value);

  return (
    <div className="py-4 border-b border-gray-50 last:border-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <label className="text-sm font-bold text-gray-800">{setting.label}</label>
        <div className="flex items-center gap-2">
          {dirty ? (
            <span className="text-[10px] font-black uppercase tracking-widest text-amber-600">
              Unsaved
            </span>
          ) : null}
          <span
            className={`text-[10px] font-black uppercase tracking-widest ${
              setting.source === "settings" ? "text-indigo-500" : "text-gray-300"
            }`}
            title={
              setting.source === "settings"
                ? "This value was set on this screen."
                : "Nobody has set this — it is the value the portal ships with."
            }
          >
            {SOURCE_LABEL[setting.source]}
          </span>
        </div>
      </div>

      {setting.description ? (
        <p className="mt-1 text-xs text-gray-500 leading-relaxed">{setting.description}</p>
      ) : null}

      <div className="mt-2 flex items-center gap-2">
        {setting.readonly ? (
          <div
            className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-100 bg-gray-50 text-sm font-bold text-gray-500 tabular-nums"
            title="Written by the portal when numbers are issued. Moving it by hand is how a number gets handed out twice."
          >
            <Lock className="w-3.5 h-3.5 text-gray-300 shrink-0" />
            {draft === "" ? <span className="font-normal text-gray-400">Nothing issued yet</span> : draft}
          </div>
        ) : (
          <>
            <input
              value={draft}
              onChange={(e) => onChange(e.target.value)}
              inputMode={setting.type === "string" ? "text" : "decimal"}
              placeholder={
                setting.default === null || setting.default === ""
                  ? "No requirement"
                  : String(setting.default)
              }
              className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-gray-100"
            />
            <Unit unit={setting.unit} />
            {/* Clearing is a real action — it removes the override and puts the
                shipped default back — so it gets its own button rather than
                being something you discover by emptying the box. */}
            <button
              onClick={() => onChange("")}
              disabled={draft === ""}
              title="Clear this and fall back to the default"
              className="p-2 rounded-xl border border-gray-100 text-gray-400 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>

      {!setting.readonly ? (
        <p className="mt-1.5 text-[11px] text-gray-400">
          {defaultHint(setting)}
          {setting.unit === "naira" && draft && !Number.isNaN(Number(draft))
            ? ` Currently ${naira(Number(draft))}.`
            : ""}
        </p>
      ) : null}
    </div>
  );
}

/** Where a running series stands, and which source decided it. */
function SeriesCard({ state }: { state: SeriesState }) {
  const SOURCE: Record<string, string> = {
    register: "the register",
    settings: "the counter saved here",
    configured_start: "the configured start — nothing issued yet",
  };

  return (
    <div className="rounded-2xl border border-gray-100 p-4">
      <div className="flex items-center gap-2">
        <Hash className="w-3.5 h-3.5 text-indigo-500" />
        <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">
          {state.series_label} series
        </div>
      </div>
      <div className="mt-2 text-2xl font-black text-gray-900 tabular-nums">{state.next_text}</div>
      <div className="text-[11px] font-semibold text-gray-400">next to be issued</div>
      <div className="mt-3 space-y-0.5 text-[11px] text-gray-500">
        <div>
          Last issued: <b className="text-gray-700">{state.highest_text ?? "none"}</b>
        </div>
        <div>From {SOURCE[state.seeded_from] ?? state.seeded_from}</div>
        {/* Named separately, because the two disagreeing is the one thing worth
            noticing here: issuing always takes the higher of them, so a counter
            behind the register is harmless but worth seeing. */}
        {state.register_highest !== null && state.recorded_last !== null &&
        state.register_highest !== state.recorded_last ? (
          <div className="text-amber-600 font-semibold">
            Register holds {state.register_highest}, counter says {state.recorded_last} — the
            higher one is used.
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** The form value for a setting — null and 0 are different things, so "" only for null. */
function toInput(value: string | number | null): string {
  return value === null || value === undefined ? "" : String(value);
}

export default function PortalSettingsPage() {
  const { data, isLoading, isError, error, refetch, isFetching } = usePortalSettingsQuery();
  const [save, { isLoading: saving }] = useSavePortalSettingsMutation();

  const groups: PortalSettingGroup[] = useMemo(() => data?.data?.groups ?? [], [data]);
  const series: Record<string, SeriesState> = data?.data?.series ?? {};
  const storageReady: boolean = data?.data?.storage_ready ?? true;

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);

  // Reseeded whenever the server's values change — after a save, or a reload in
  // another tab. Keyed off the values themselves rather than off a fetch flag,
  // so typing is never wiped by a background refetch that changed nothing.
  const serverValues = useMemo(() => {
    const map: Record<string, string> = {};
    for (const g of groups) for (const s of g.settings) map[s.key] = toInput(s.value);
    return map;
  }, [groups]);

  useEffect(() => {
    setDrafts(serverValues);
  }, [serverValues]);

  const dirty = useMemo(
    () => Object.keys(drafts).filter((k) => drafts[k] !== (serverValues[k] ?? "")),
    [drafts, serverValues],
  );

  const editable = useMemo(() => {
    const map: Record<string, PortalSetting> = {};
    for (const g of groups) for (const s of g.settings) map[s.key] = s;
    return map;
  }, [groups]);

  const doSave = async () => {
    setBanner(null);
    try {
      const res: any = await save({
        settings: dirty
          // The counters are shown, never sent — the server refuses them
          // anyway, and sending them would make every save report rejections.
          .filter((key) => !editable[key]?.readonly)
          .map((key) => ({ key, value: drafts[key] === "" ? null : drafts[key] })),
      }).unwrap();

      setBanner({ ok: true, text: res?.message ?? "Saved." });
    } catch (e: any) {
      setBanner({
        ok: false,
        text: e?.data?.message || "Nothing was saved. Please try again.",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <div className="h-24 bg-gray-100 rounded-3xl animate-pulse" />
        <div className="h-64 bg-gray-100 rounded-3xl animate-pulse" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white border border-rose-100 rounded-3xl p-8 text-center">
          <AlertCircle className="w-8 h-8 text-rose-400 mx-auto" />
          <h2 className="mt-3 font-black text-gray-900">Settings could not be loaded</h2>
          <p className="mt-1 text-sm text-gray-500">
            {(error as any)?.data?.message ?? "The server did not return the settings."}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 rounded-xl border text-sm font-bold hover:bg-gray-50"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-6">
      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
              <SettingsIcon className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight">Portal Settings</h1>
              <p className="text-sm font-semibold text-gray-500">
                Fees, eligibility bars and the shape of the Institute's numbers — changed here
                rather than by a deploy.
              </p>
            </div>
          </div>
          <button
            onClick={doSave}
            disabled={saving || dirty.length === 0 || !storageReady}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-bold hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save {dirty.length ? `${dirty.length} change${dirty.length === 1 ? "" : "s"}` : ""}
          </button>
        </div>

        {/* Before the migration lands every value on this screen is the shipped
            default and nothing typed in would be kept — said plainly rather
            than discovered when a save fails. */}
        {!storageReady ? (
          <div className="mt-4 px-4 py-3 rounded-2xl border border-amber-100 bg-amber-50 text-amber-800 text-sm font-semibold">
            Settings are not set up on this server yet — the settings migration has not been run.
            Everything below is the value the portal ships with, and nothing can be saved.
          </div>
        ) : null}
      </div>

      {banner ? (
        <div
          className={`px-4 py-3 rounded-2xl text-sm font-semibold border ${
            banner.ok
              ? "bg-emerald-50 border-emerald-100 text-emerald-800"
              : "bg-rose-50 border-rose-100 text-rose-800"
          }`}
        >
          <span className="inline-flex items-center gap-2">
            {banner.ok ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {banner.text}
          </span>
        </div>
      ) : null}

      {/* Where each running series stands. Above the numbering settings, because
          it is what makes a change to a prefix or a width readable. */}
      {Object.keys(series).length > 0 ? (
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <h2 className="text-lg font-black text-gray-900">Where the series stand</h2>
          <p className="text-sm text-gray-500 mb-4">
            Issuing always takes the higher of the counter saved here and the largest number the
            register actually holds, so a counter that falls behind can never hand out a number
            someone already has.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.values(series).map((s) => (
              <SeriesCard key={s.series} state={s} />
            ))}
          </div>
        </div>
      ) : null}

      {/* The settings themselves */}
      {groups.map((group) => (
        <div key={group.group} className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <h2 className="text-lg font-black text-gray-900">{group.label}</h2>
          {group.description ? (
            <p className="text-sm text-gray-500">{group.description}</p>
          ) : null}

          <div className="mt-3">
            {group.settings.map((s) => (
              <Field
                key={s.key}
                setting={s}
                draft={drafts[s.key] ?? ""}
                onChange={(v) => setDrafts((d) => ({ ...d, [s.key]: v }))}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Repeated at the foot: the groups are long, and a save button only at
          the top is a save button you scroll away from. */}
      <div className="flex items-center justify-end gap-3">
        {dirty.length > 0 ? (
          <button
            onClick={() => setDrafts(serverValues)}
            className="px-4 py-2.5 rounded-xl border text-sm font-bold hover:bg-gray-50"
          >
            Discard changes
          </button>
        ) : null}
        <button
          onClick={doSave}
          disabled={saving || dirty.length === 0 || !storageReady}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-bold hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving || isFetching ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save {dirty.length ? `${dirty.length} change${dirty.length === 1 ? "" : "s"}` : ""}
        </button>
      </div>
    </div>
  );
}
